import { useEffect, useMemo, useState } from "react";
import { http } from "../api";
import { ArrowRight, Check, X, Globe, LogIn, Sparkles, ChevronDown, IndianRupee, DollarSign } from "lucide-react";

const COPY = {
  bn: {
    eyebrow: "প্ল্যান ও প্রাইসিং",
    heading: "আপনার জন্য সঠিক প্ল্যান বেছে নিন",
    subhead: "প্রতিটি প্ল্যান নবদ্বীপ · ওয়ার্ড ৭ কমিউনিটি টিমের জন্য তৈরি।",
    toggle_monthly: "মাসিক",
    toggle_yearly: "বার্ষিক",
    yearly_save: "সঞ্চয়",
    per_month: "/ মাস",
    per_year: "/ বছর",
    cta: "শুরু করুন",
    back: "ল্যান্ডিং-এ ফিরুন",
    login: "লগইন",
    empty: "শীঘ্রই মূল্য প্রকাশিত হবে।",
    no_features: "কোনও ফিচার এখনও যুক্ত হয়নি",
    compare_title: "প্ল্যান তুলনা করুন",
    compare_sub: "প্রতিটি টিয়ার-এ কী কী রয়েছে এক নজরে দেখুন।",
    compare_col: "ফিচার",
    faq_title: "সচরাচর জিজ্ঞাস্য",
    faq_sub: "ইমেল করার আগে এখানে দেখুন —",
    faq_rate_note: "লাইভ USD রেট",
    faq_rate_fallback: "আনুমানিক USD রেট",
    faqs: [
      { q: "বিলিং কীভাবে কাজ করে?",
        a: "প্রতিটি প্ল্যান মাসিক বা বার্ষিক চার্জ হয়। ভারতীয় ব্যবহারকারীদের জন্য UPI, কার্ড এবং নেট-ব্যাঙ্কিং সমর্থিত। ইনভয়েস প্রতিটি চক্রে ইমেলে পাঠানো হয়।" },
      { q: "আমি কি যেকোনো সময় বাতিল করতে পারি?",
        a: "হ্যাঁ। বাতিল করার পরেও বর্তমান বিলিং চক্রের শেষ পর্যন্ত অ্যাক্সেস থাকবে — কোনও প্রশ্ন নয়, কোনও দেরি নয়।" },
      { q: "আমার ডেটা কি এক্সপোর্ট করা যাবে?",
        a: "যেকোনো সময় সেটিংস → এক্সপোর্ট থেকে সম্পূর্ণ CSV/JSON ডাউনলোড করুন — যোগাযোগ, অভিযোগ, ইভেন্ট, ফর্মের প্রতিক্রিয়া সবই।" },
      { q: "রিফান্ড নীতি কী?",
        a: "প্রথম ১৪ দিনের মধ্যে বার্ষিক প্ল্যান বাতিল করলে সম্পূর্ণ রিফান্ড পাবেন। মাসিক প্ল্যান প্রো-রেটেড নয়।" },
      { q: "প্ল্যান বদলাতে বা আপগ্রেড করতে পারি?",
        a: "যেকোনো সময় সেটিংস → প্ল্যান থেকে আপগ্রেড বা ডাউনগ্রেড করুন। প্রো-রেটেড ক্রেডিট স্বয়ংক্রিয়ভাবে প্রয়োগ হবে।" },
    ],
  },
  en: {
    eyebrow: "Plans & pricing",
    heading: "Pick the right plan for your team",
    subhead: "Every tier is built for Nabadwip · Ward 7 community teams.",
    toggle_monthly: "Monthly",
    toggle_yearly: "Yearly",
    yearly_save: "Save",
    per_month: "/ month",
    per_year: "/ year",
    cta: "Get started",
    back: "Back to landing",
    login: "Log in",
    empty: "Pricing coming soon.",
    no_features: "No features listed yet",
    compare_title: "Compare plans",
    compare_sub: "A side-by-side breakdown of what each tier includes.",
    compare_col: "Feature",
    faq_title: "Frequently asked",
    faq_sub: "Quick answers before you email us —",
    faq_rate_note: "Live USD rate",
    faq_rate_fallback: "Approx. USD rate",
    faqs: [
      { q: "How does billing work?",
        a: "Every plan is billed monthly or yearly. Indian customers can pay with UPI, cards, or net-banking. Invoices are emailed each cycle." },
      { q: "Can I cancel any time?",
        a: "Yes. After cancellation you keep access until the end of the current billing cycle — no questions, no delays." },
      { q: "Can I export my data?",
        a: "Full CSV/JSON export any time from Settings → Export — contacts, complaints, events, form responses, all yours." },
      { q: "What is the refund policy?",
        a: "Cancel within the first 14 days of an annual plan for a full refund. Monthly plans are not pro-rated." },
      { q: "Can I switch or upgrade plans?",
        a: "Upgrade or downgrade any time from Settings → Plan. Pro-rated credits are applied automatically." },
    ],
  },
};

const STATIC_USD_RATE = 83; // fallback if the live rate API is unreachable

async function fetchUsdInrRate() {
  // Try open.er-api.com (no API key needed). Falls back to static rate on any failure.
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3500);
    const res = await fetch("https://open.er-api.com/v6/latest/USD", { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) throw new Error("bad status");
    const data = await res.json();
    const inr = data?.rates?.INR;
    if (typeof inr === "number" && inr > 40 && inr < 200) {
      return { rate: inr, live: true };
    }
  } catch { /* fall through */ }
  return { rate: STATIC_USD_RATE, live: false };
}

function formatInr(n) {
  const num = Number(n || 0);
  return "₹" + num.toLocaleString("en-IN");
}

function formatUsd(inr, rate) {
  const num = Number(inr || 0) / (rate || STATIC_USD_RATE);
  if (num === 0) return "$0";
  const rounded = num >= 100 ? Math.round(num) : Math.round(num * 100) / 100;
  return "$" + rounded.toLocaleString("en-US", {
    minimumFractionDigits: rounded >= 100 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function savingsPercent(monthly, yearly) {
  const m = Number(monthly || 0), y = Number(yearly || 0);
  if (m <= 0 || y <= 0) return 0;
  const full = m * 12;
  if (full <= y) return 0;
  return Math.round(((full - y) / full) * 100);
}

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("landing_lang") || "en");
  const [cycle, setCycle] = useState("monthly");
  const [currency, setCurrency] = useState("INR");
  const [usdRate, setUsdRate] = useState({ rate: STATIC_USD_RATE, live: false });
  const [openFaq, setOpenFaq] = useState(null);

  const t = COPY[lang] || COPY.en;

  useEffect(() => {
    http.get("/public/plans").then((r) => setPlans(r.data.plans || [])).finally(() => setLoading(false));
    http.get("/branding/effective").then((r) => {
      const b = r.data || {};
      setBrand(b);
      const root = document.documentElement;
      if (b.primary_color) root.style.setProperty("--teal", b.primary_color);
      if (b.accent_color) root.style.setProperty("--amber", b.accent_color);
    }).catch(() => {});
    fetchUsdInrRate().then(setUsdRate);
  }, []);

  useEffect(() => { localStorage.setItem("landing_lang", lang); }, [lang]);

  // Send prospects back to the Landing signup modal
  const openSignup = () => { window.location.href = "/#register"; };
  const openLogin = () => { window.location.href = "/#login"; };

  const cards = useMemo(() => plans, [plans]);

  const compareMatrix = useMemo(() => {
    // Build a stable list of unique feature labels (first appearance order across plans)
    const rows = [];
    const seen = new Set();
    for (const p of plans) {
      for (const f of p.features || []) {
        const key = (f.label || "").trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        rows.push(f.label);
      }
    }
    // For each row, resolve every plan's status
    return rows.map((label) => ({
      label,
      cells: plans.map((p) => {
        const f = (p.features || []).find((x) => (x.label || "").trim().toLowerCase() === label.trim().toLowerCase());
        if (!f) return { included: false, limit: null, missing: true };
        return { included: !!f.included, limit: f.limit || null, missing: false };
      }),
    }));
  }, [plans]);

  return (
    <div className="lp" data-testid="pricing-root" lang={lang}>
      <header className="lp-nav" data-testid="pricing-nav">
        <div className="lp-brand" onClick={() => (window.location.href = "/")}>
          {brand?.logo_url ? (
            <span className="lp-mark"><img src={brand.logo_url} alt="" style={{ width: 22, height: 22, objectFit: "contain" }} /></span>
          ) : (
            <span className="lp-mark">৭</span>
          )}
          <div>
            <b>{brand?.brand_name || "Ward 7"}</b>
            <small>{(lang === "bn" ? brand?.brand_tagline_bn : brand?.brand_tagline_en) || brand?.brand_tagline_en || "Nabadwip · নবদ্বীপ"}</small>
          </div>
        </div>
        <nav className="lp-nav-links">
          <a href="/" data-testid="pricing-back-link">{t.back}</a>
        </nav>
        <div className="lp-nav-cta">
          <button className="lp-lang-toggle" data-testid="pricing-lang-toggle" onClick={() => setLang(lang === "bn" ? "en" : "bn")}>
            <Globe size={13} /> {lang === "bn" ? "EN" : "বাং"}
          </button>
          <button className="lp-btn-ghost" data-testid="pricing-login" onClick={openLogin}>
            <LogIn size={13} /> {t.login}
          </button>
        </div>
      </header>

      <section className="pricing-hero" data-testid="pricing-hero">
        <p className="lp-eyebrow">{t.eyebrow}</p>
        <h1>{t.heading}</h1>
        <p className="pricing-sub">{t.subhead}</p>

        <div className="pricing-toggle" data-testid="pricing-cycle-toggle">
          <button
            className={cycle === "monthly" ? "on" : ""}
            data-testid="pricing-cycle-monthly"
            onClick={() => setCycle("monthly")}
          >
            {t.toggle_monthly}
          </button>
          <button
            className={cycle === "yearly" ? "on" : ""}
            data-testid="pricing-cycle-yearly"
            onClick={() => setCycle("yearly")}
          >
            {t.toggle_yearly}
          </button>
        </div>

        <div className="pricing-currency" data-testid="pricing-currency-toggle">
          <button
            className={currency === "INR" ? "on" : ""}
            data-testid="pricing-currency-inr"
            onClick={() => setCurrency("INR")}
          >
            <IndianRupee size={11} /> INR
          </button>
          <button
            className={currency === "USD" ? "on" : ""}
            data-testid="pricing-currency-usd"
            onClick={() => setCurrency("USD")}
          >
            <DollarSign size={11} /> USD
          </button>
          {currency === "USD" && (
            <small className="pricing-rate-note" data-testid="pricing-rate-note">
              {usdRate.live ? t.faq_rate_note : t.faq_rate_fallback} · 1 USD ≈ ₹{usdRate.rate.toFixed(2)}
            </small>
          )}
        </div>
      </section>

      <section className="pricing-grid-wrap" data-testid="pricing-grid-wrap">
        {loading ? (
          <p className="pricing-empty">Loading…</p>
        ) : cards.length === 0 ? (
          <p className="pricing-empty" data-testid="pricing-empty"><Sparkles size={14} /> {t.empty}</p>
        ) : (
          <div className="pricing-grid" data-testid="pricing-grid">
            {cards.map((p, idx) => {
              const highlight = idx === 1 && cards.length >= 3; // middle card feature
              const price = cycle === "yearly" ? p.price_yearly : p.price_monthly;
              const suffix = cycle === "yearly" ? t.per_year : t.per_month;
              const save = cycle === "yearly" ? savingsPercent(p.price_monthly, p.price_yearly) : 0;
              const priceDisplay = currency === "USD" ? formatUsd(price, usdRate.rate) : formatInr(price);
              return (
                <article
                  key={p.plan_id}
                  className={`pricing-card ${highlight ? "featured" : ""}`}
                  data-testid={`pricing-card-${p.plan_id}`}
                >
                  {highlight && <div className="pricing-badge">Popular</div>}
                  <h3>{p.name}</h3>
                  <div className="pricing-price">
                    <span data-testid={`pricing-price-${p.plan_id}`}>{priceDisplay}</span>
                    <em>{suffix}</em>
                  </div>
                  {save > 0 && (
                    <div className="pricing-save-badge" data-testid={`pricing-save-${p.plan_id}`}>
                      {t.yearly_save} {save}%
                    </div>
                  )}
                  {(!p.features || p.features.length === 0) ? (
                    <p className="pricing-empty-features">{t.no_features}</p>
                  ) : (
                    <ul className="pricing-features" data-testid={`pricing-features-${p.plan_id}`}>
                      {p.features.map((f, i) => (
                        <li key={i} className={f.included ? "" : "off"}>
                          {f.included ? <Check size={13} /> : <X size={13} />}
                          <span>{f.label}{f.limit ? <em> — {f.limit}</em> : null}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    className={`pricing-cta ${highlight ? "primary" : "outline"}`}
                    data-testid={`pricing-cta-${p.plan_id}`}
                    onClick={openSignup}
                  >
                    {t.cta} <ArrowRight size={13} />
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {cards.length > 0 && compareMatrix.length > 0 && (
        <section className="pricing-compare-wrap" id="compare" data-testid="pricing-compare">
          <div className="pricing-faq-head">
            <p className="lp-eyebrow">COMPARE</p>
            <h2>{t.compare_title}</h2>
            <p className="pricing-sub">{t.compare_sub}</p>
          </div>
          <div className="pricing-compare-scroll">
            <table className="pricing-compare-table" data-testid="pricing-compare-table">
              <thead>
                <tr>
                  <th className="pricing-compare-firstcol">{t.compare_col}</th>
                  {cards.map((p) => (
                    <th key={p.plan_id} data-testid={`pricing-compare-col-${p.plan_id}`}>
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {compareMatrix.map((row, i) => (
                  <tr key={i} data-testid={`pricing-compare-row-${i}`}>
                    <td className="pricing-compare-firstcol">{row.label}</td>
                    {row.cells.map((c, j) => (
                      <td key={j} data-testid={`pricing-compare-cell-${i}-${j}`}>
                        {c.included ? (
                          <span className="pricing-compare-yes">
                            <Check size={13} />
                            {c.limit ? <em>{c.limit}</em> : null}
                          </span>
                        ) : (
                          <span className="pricing-compare-no"><X size={13} /></span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="pricing-faq-wrap" id="faq" data-testid="pricing-faq">
        <div className="pricing-faq-head">
          <p className="lp-eyebrow">FAQ</p>
          <h2>{t.faq_title}</h2>
          <p className="pricing-sub">{t.faq_sub}</p>
        </div>
        <ul className="pricing-faq-list" data-testid="pricing-faq-list">
          {t.faqs.map((f, i) => {
            const open = openFaq === i;
            return (
              <li
                key={i}
                className={`pricing-faq-item ${open ? "on" : ""}`}
                data-testid={`pricing-faq-item-${i}`}
              >
                <button
                  className="pricing-faq-q"
                  data-testid={`pricing-faq-q-${i}`}
                  onClick={() => setOpenFaq(open ? null : i)}
                  aria-expanded={open}
                >
                  <span>{f.q}</span>
                  <ChevronDown size={16} className="pricing-faq-chevron" />
                </button>
                <div className="pricing-faq-a" data-testid={`pricing-faq-a-${i}`}>
                  <p>{f.a}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
